import {
    Container, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, Switch, Typography,
} from '@mui/material';
import {
    Title, Fingerprint, MailOutline, PhoneOutlined,
} from '@mui/icons-material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';

import { AxiosResponse } from 'axios';
import { LoggedInUser } from '../../../types/auth.types';
import Button from '../foundation/Button/Button';
import { ShadowBGContainer } from '../foundation/Containers';
import CustomBR from '../foundation/CustomBR';
import TextField from '../foundation/TextField';
import Page from '../layout/base.page';
import UserNotification, { UserNotificationProps } from '../components/UserNotification';
import { Flexbox } from '../foundation/Flex';
import {
    CompanyAutocomplete, GeoAutocomplete, LocationOption, TagsSearchAutocomplete,
} from '../components/AutocompleteRemote';
import TextArea from '../foundation/TextArea';
import Checkbox from '../foundation/Checkbox';
import { TagViewModel } from '../../../types/tags.types';
import RemovableTag, { AddableTag } from '../foundation/RemovableTag';
import { createNewJob, editJob, getTags } from '../../api/job.api';
import { updateQueryStringParameter } from '../../utils/urlUtils';
import Anchor from '../foundation/Anchor';
import { CompanyViewModel } from '../../../mappers/companyMapper';
import { JobViewModel } from '../../../types/jobs.types';
import Alert from '../foundation/Alert';
import {
    JOB_CONTACT_EMAIL_MAX_LENGTH,
    JOB_CONTACT_PHONE_MAX_LENGTH,
    JOB_DESCRIPTION_MAX_LENGTH,
    JOB_DESCRIPTION_MIN_LENGTH,
    JOB_EXTERNAL_ID_MAX_LENGTH,
    JOB_TITLE_MAX_LENGTH,
} from '../../../common/jobValidation.constants';

export type UserPostJobPageProps = {
    url: string
    user?: LoggedInUser
    job?: JobViewModel
    safeid?: string
    locationAbroadChecked?: boolean,
    locationAllStateChecked?: boolean
    numberOfJobsRelatedToUnkownUser?: number
    totalPromotions?: number
    managedCompanies?: CompanyViewModel[]
}

interface IFormPostJob {
    title: string
    description: string
    phone: string
    locationAllStateChecked?: boolean
    locationAbroadChecked?: boolean
    email?: string
    employerJobid: string
    jobPeriod: number
    cvMandatory?: boolean
    acceptsSystemMatches?: boolean
    company?: string
}

const BoxBorder = styled('div')`
    border: 1px solid #bfbfc1;
    padding: 0.5rem 1rem;
    border-radius: 10px;
`;

const INTERNAL_JOB_PERIOD = 30;
const DEFAULT_JOB_PERIOD = 1; // * 4 weeks

const UserPostJobPage = (props: UserPostJobPageProps) => {
    const editMode = !!props.job;
    const [notification, setNotification] = useState<UserNotificationProps>(null);
    const [jobCreated, setJobCreated] = useState<boolean>(false);
    const [jobTags, setJobTags] = useState<TagViewModel[]>(props.job?.tags || []);
    const [recommendedTags, setRecommendedTags] = useState<TagViewModel[]>([]);
    const defaultLocationAbroadChecked = props.job?.formatted_address === 'חו"ל';
    const defaultLocationAllStateChecked = !props.safeid && props.job?.formatted_address === 'כל הארץ';
    const {
        register, handleSubmit, formState: { errors }, watch,
    } = useForm<IFormPostJob>({
        defaultValues: {
            locationAbroadChecked: defaultLocationAbroadChecked,
            locationAllStateChecked: defaultLocationAllStateChecked,
            jobPeriod: DEFAULT_JOB_PERIOD,
        },
    });

    const fetchRecommendedTags = async () => {
        const text = `${watch('title')} ${watch('description')}`;
        const tagsRes = await getTags({ text });
        const currentTagIds = jobTags.map(t => t.tagid);

        if (currentTagIds && currentTagIds.length > 0) {
            setRecommendedTags(tagsRes.data?.filter(tag => currentTagIds.indexOf(tag.tagid) === -1));
        } else {
            setRecommendedTags(tagsRes.data);
        }
    };

    const defaultManagedCompany = !editMode && props.managedCompanies?.length === 1
        ? props.managedCompanies[0]
        : undefined;
    const [company, setCompany] = useState<Partial<CompanyViewModel>>(
        props.job?.company ?? defaultManagedCompany,
    );
    const companyChange = (_ev, val) => { setCompany(val); };
    const companyInputChange = (_ev, val) => { setCompany({ name: val }); };

    const [location, setLocation] = useState<Partial<LocationOption> & { formatted_address: string }>(props.job ? {
        latitude: props.job.latitude,
        longitude: props.job.longitude,
        formatted_address: props.job.formatted_address,
    } as LocationOption : null);
    const locationChange = (_ev, val) => { setLocation(val); };
    const locationInputChange = (_ev, newVal) => { setLocation({ formatted_address: newVal }); };

    const onSubmit = async (data: IFormPostJob) => {
        try {
            let locationData = location;
            if (!jobTags?.length || jobTags.length > 8) {
                setNotification({
                    severity: 'error',
                    message: 'יש להזין מינימום תגית אחת וניתן להזין עד שמונה תגיות',
                    id: Math.random(),
                });
                return;
            }

            if (!data.locationAbroadChecked && !data.locationAllStateChecked && !location?.latitude) {
                setNotification({
                    severity: 'error',
                    message: 'חובה להזין מיקום משרה ולבחור מהרשימה או לציין עבודה מהבית \\ בחוץ לארץ, מומלץ להזין מיקום מדויק.',
                    id: Math.random(),
                });
                return;
            }

            if (data.locationAbroadChecked) {
                locationData = {
                    formatted_address: 'חו"ל',
                    latitude: null,
                    longitude: null,
                };
            } else if (data.locationAllStateChecked) {
                locationData = {
                    formatted_address: 'כל הארץ',
                    latitude: null,
                    longitude: null,
                };
            }

            let jobRes: AxiosResponse<JobViewModel>;

            if (editMode) {
                jobRes = await editJob({ jobid: props.job.jobid }, {
                    jobid: props.job.jobid,
                    ...locationData,
                    ...data,
                    companyid: company?.companyid,
                    companyName: company?.name,
                    tags: jobTags.map(tag => tag.tagid),
                }, { safeid: props.safeid }, true);
                setNotification({
                    id: Math.random(),
                    children: <><Typography>משרת העבודה נשמרה בהצלחה ותעודכן באתר בדקות הקרובות</Typography></>,
                    severity: 'success',
                });
            } else {
                jobRes = await createNewJob({
                    ...locationData,
                    ...data,
                    companyid: company?.companyid,
                    company: company?.name,
                    jobPeriod: (data.jobPeriod || 1) * INTERNAL_JOB_PERIOD,
                    promotion: 1,
                    tags: jobTags.map(tag => tag.tagid),
                }, true);
                setJobCreated(true);
                setNotification({
                    id: Math.random(),
                    children: <>
                        <Typography>משרת העבודה נשלחה בהצלחה ותפורסם באתר בדקות הקרובות!</Typography>
                        <Anchor text="עבור לעריכת המשרה" href={`/user/editJob/${jobRes.data.jobid}`} />
                        &nbsp;|&nbsp;
                        <Anchor text="עבור לניהול המשרות" href={'/backoffice/employers/manage-jobs'} />
                    </>,
                    severity: 'success',
                });
            }
        } catch (error) {
            if (error?.response?.status === 409) {
                setNotification({
                    id: Math.random(),
                    children: (<div>
                        <Typography>לא ניתן לשמור את המשרה מכיוון שמשרה זהה קיימת כבר.</Typography>
                        <Anchor href={`/user/editJob/${error.response.data?.id}`} text="קישור למשרה הקיימת" blank />
                    </div>),
                    severity: 'error',
                });
            } else {
                setNotification({
                    id: Math.random(),
                    message: error.response?.data || error.message,
                    severity: 'error',
                });
            }
        }
    };

    const filteredRecommnededTags = recommendedTags.filter(rTag => !jobTags.find(jTag => jTag.tagid === rTag.tagid));

    const inputTitle = register('title', { required: true, maxLength: JOB_TITLE_MAX_LENGTH });
    const locationAbroadCheckedInput = register('locationAbroadChecked');
    const locationAllStateCheckedInput = register('locationAllStateChecked');
    const descriptionInput = register('description', {
        required: true,
        minLength: JOB_DESCRIPTION_MIN_LENGTH,
        maxLength: JOB_DESCRIPTION_MAX_LENGTH,
    });
    const emailInput = register('email', {
        maxLength: {
            value: JOB_CONTACT_EMAIL_MAX_LENGTH,
            message: `כתובת האימייל יכולה להכיל עד ${JOB_CONTACT_EMAIL_MAX_LENGTH} תווים`,
        },
        pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'כתובת אימייל לא תקינה',
        },
    });
    const phoneInput = register('phone', {
        maxLength: {
            value: JOB_CONTACT_PHONE_MAX_LENGTH,
            message: `מספר הטלפון יכול להכיל עד ${JOB_CONTACT_PHONE_MAX_LENGTH} תווים`,
        },
    });
    const employerJobidInput = register('employerJobid', {
        maxLength: {
            value: JOB_EXTERNAL_ID_MAX_LENGTH,
            message: `מספר מזהה המשרה יכול להכיל עד ${JOB_EXTERNAL_ID_MAX_LENGTH} תווים`,
        },
    });
    const cvMandatoryInput = register('cvMandatory');
    const acceptsSystemMatchesInput = register('acceptsSystemMatches');

    const isUnauthenticatedUser = props.user && !props.user.roles.includes(5);

    if (isUnauthenticatedUser) {
        return <Page
            url={props.url}
            user={props.user}
            pageHeaderTitle={editMode ? 'Mploy - עריכת פרטי המשרה' : 'Mploy - פרסום משרה חדשה'}>
            {isUnauthenticatedUser && <Container>
                <Alert severity="warning">
                    <>
                        <Typography>
                        שים לב: נראה כי כתובת המייל שלך איננה מאומתת. ללא אימות כתובת המייל לא תוכל לפרסם משרות באתר.
                        </Typography>
                        <Typography>
                        יש לבדוק את תיבת המייל שלך (כולל בתיקיית הספאם) על מנת לאמת את כתובת המייל.
                        </Typography>
                        <CustomBR h={10} />
                        <Anchor href="/backoffice/employers/profile" text="ניהול הפרופיל שלי" />
                    </>
                </Alert>
                <CustomBR h={20} />
            </Container>}
        </Page>;
    }

    return <Page
        url={props.url}
        user={props.user}
        pageHeaderTitle={editMode ? 'Mploy - עריכת פרטי המשרה' : 'Mploy - פרסום משרה חדשה'}>
        <Container>
            <CustomBR />
            {editMode && !props.user && props.numberOfJobsRelatedToUnkownUser && <>
                <UserNotification
                    message={`מצאנו עוד ${props.numberOfJobsRelatedToUnkownUser} משרות המשויכות לכתובת המייל: ${props.job.email}. `}
                    severity="info"
                    id="more-jobs"
                >
                    <div>
                        <CustomBR h={10} />
                        <Button
                            size="small"
                            onClick={() => {
                                window.history.pushState({}, '', updateQueryStringParameter('redirect', '/backoffice'));
                                window.modals?.toggleRegisterModal();
                            }}
                            variant="outlined">הירשם וקבל שליטה על כל המשרות שלך</Button>
                    </div>
                </UserNotification>
                <CustomBR />
            </>}
            {notification && <><UserNotification {...notification} /><CustomBR /></>}
            {props.user && <Flexbox flexDirection="row-reverse">
                <Button onClick={() => window.location.assign('/backoffice/employers/manage-jobs')} color="primary">ניהול המשרות שלי</Button>
                {editMode && <>&nbsp;&nbsp;<Button onClick={() => window.location.assign('/user/postJobs')} variant="outlined">פרסום משרה נוספת</Button></>}
            </Flexbox>}
            <CustomBR h={10} />
            {editMode && <>
                <Typography display="inline">קישור  למשרה באתר:&nbsp;</Typography>
                <Anchor text={props.job.title} blank href={`/job/details/${props.job.jobid}`} />
            </>}
            {!jobCreated && <><CustomBR h={10} />
                <ShadowBGContainer>
                    <TextField
                        defaultValue={props.job?.title}
                        required
                        {...inputTitle}
                        inputRef={inputTitle.ref}
                        name="title"
                        id="title"
                        label="כותרת המשרה"
                        type="text"
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.type === 'maxLength'
                            ? `כותרת המשרה יכולה להכיל עד ${JOB_TITLE_MAX_LENGTH} תווים`
                            : errors.title && 'יש להזין כותרת'}
                        inputProps={{ maxLength: JOB_TITLE_MAX_LENGTH }}
                        InputProps={{ endAdornment: <Title /> }}
                    />
                    <CustomBR />
                    <GeoAutocomplete
                        required
                        onChange={locationChange}
                        onInputChange={locationInputChange}
                        defaultValue={location?.formatted_address}
                        disabled={watch('locationAllStateChecked') || watch('locationAbroadChecked')}
                        helperText="מיקום המשרה, מומלץ לרשום מיקום מדויק"
                    />
                    <CustomBR />
                    <Grid spacing={2} container>
                        <Grid item sm={6} xs={12}>
                            <BoxBorder>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox
                                            defaultChecked={defaultLocationAllStateChecked}
                                            disabled={watch('locationAbroadChecked')}
                                            {...locationAllStateCheckedInput}
                                            inputRef={locationAllStateCheckedInput.ref}
                                            name="locationAllStateChecked"
                                        />}
                                        label="המשרה בכל הארץ \ מהבית"
                                    />
                                </FormGroup>
                            </BoxBorder>
                        </Grid>
                        <Grid item sm={6} xs={12}>
                            <BoxBorder>
                                <FormGroup>
                                    <FormControlLabel
                                        control={<Checkbox
                                            defaultChecked={defaultLocationAbroadChecked}
                                            disabled={watch('locationAllStateChecked')}
                                            {...locationAbroadCheckedInput}
                                            inputRef={locationAbroadCheckedInput.ref}
                                            name="locationAbroadChecked"
                                        />}
                                        label="המשרה בחוץ לארץ"
                                    />
                                </FormGroup>
                            </BoxBorder>
                        </Grid>
                    </Grid>
                    <CustomBR />
                    <TextArea
                        defaultValue={props.job?.description}
                        {...descriptionInput}
                        inputRef={descriptionInput.ref}
                        required
                        onBlur={fetchRecommendedTags}
                        label="תיאור המשרה"
                        name="description"
                        id="description"
                        helperText={errors.description
                            && `יש להזין תיאור משרה בין ${JOB_DESCRIPTION_MIN_LENGTH} ל ${JOB_DESCRIPTION_MAX_LENGTH} תווים. ניתן להשתמש ב Markdown.`}
                        fullWidth
                        rows={5}
                        maxLength={JOB_DESCRIPTION_MAX_LENGTH}
                    />
                    <CustomBR />
                </ShadowBGContainer>
                <CustomBR />
                <ShadowBGContainer>
                    <Grid container spacing={1}>
                        <Grid item md={6} xs={12}>
                            <TextField
                                defaultValue={props.job?.email || ''}
                                {...emailInput}
                                inputRef={emailInput.ref}
                                name="email"
                                id="email"
                                // label={`אימייל לקבל פניות${!allowAdvancedFeatures ? ' (זמין במשרות פרמיום)' : ''}`}
                                label={'אימייל לקבל פניות'}
                                type="email"
                                fullWidth
                                // disabled={!allowAdvancedFeatures}
                                error={!!errors.email}
                                helperText={errors.email?.message || (props.user && !props.job?.email && `ברירת מחדל: ${props.user.email}`)}
                                inputProps={{ maxLength: JOB_CONTACT_EMAIL_MAX_LENGTH }}
                                InputProps={{ endAdornment: <MailOutline /> }}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                defaultValue={props.job?.phone}
                                {...phoneInput}
                                inputRef={phoneInput.ref}
                                name="phone"
                                id="phone"
                                label="טלפון ליצירת קשר"
                                type="number"
                                fullWidth
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                                inputProps={{ maxLength: JOB_CONTACT_PHONE_MAX_LENGTH }}
                                InputProps={{ endAdornment: <PhoneOutlined /> }}
                            />
                        </Grid>
                    </Grid>
                    <CustomBR />
                    <Grid container spacing={1}>
                        <Grid item md={6} xs={12}>
                            <CompanyAutocomplete
                                onInputChange={companyInputChange}
                                defaultValue={company?.name}
                                helperText={defaultManagedCompany
                                    ? 'החברה המשויכת לחשבון מולאה אוטומטית'
                                    : undefined}
                                onChange={companyChange} />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                defaultValue={props.job?.employerJobid || ''}
                                {...employerJobidInput}
                                inputRef={employerJobidInput.ref}
                                name="employerJobid"
                                id="employerJobid"
                                // label={`מספר מזהה משרה${!allowAdvancedFeatures ? ' (זמין במשרות פרמיום)' : ''}`}
                                label={'מספר מזהה משרה'}
                                // disabled={!allowAdvancedFeatures}
                                helperText={errors.employerJobid?.message || 'מספר זה יצורף לכותרת האימייל ממועמדים'}
                                type="text"
                                fullWidth
                                error={!!errors.employerJobid}
                                inputProps={{ maxLength: JOB_EXTERNAL_ID_MAX_LENGTH }}
                                InputProps={{ endAdornment: <Fingerprint /> }}
                            />
                        </Grid>
                    </Grid>
                    <FormGroup>
                        <FormControl component="fieldset" variant="standard">
                            <FormControlLabel
                                control={<Switch
                                    defaultChecked={editMode ? !!props.job?.cvMandatory : true}
                                    {...cvMandatoryInput}
                                    inputRef={cvMandatoryInput.ref}
                                    name="cvMandatory"
                                    color="primary"
                                />}
                                label="חובה להגיש מועמדות עם קו''ח"
                            />
                            <FormHelperText>
                                כשמופעל, מועמדים לא יוכלו להגיש מועמדות בלי לצרף קובץ קורות חיים.
                            </FormHelperText>
                        </FormControl>
                        <CustomBR h={8} />
                        <FormControl component="fieldset" variant="standard">
                            <FormControlLabel
                                control={<Switch
                                    defaultChecked={props.job?.acceptsSystemMatches ?? true}
                                    {...acceptsSystemMatchesInput}
                                    inputRef={acceptsSystemMatchesInput.ref}
                                    name="acceptsSystemMatches"
                                    color="primary"
                                />}
                                label="קבלת המלצות AI על מועמדים זמינים שמתאימים למשרה"
                            />
                            <FormHelperText>
                                כשמופעל, המערכת תציע מועמדים רלוונטיים שזוהו אוטומטית באמצעות Mploy AI.
                            </FormHelperText>
                        </FormControl>
                    </FormGroup>
                </ShadowBGContainer>
                <CustomBR />
                <ShadowBGContainer>
                    <Typography color="secondary" variant="h6" fontWeight="bold">
                    בחירת תחומים ותפקידים למשרה
                    </Typography>
                    {jobTags?.length ? <>
                        <CustomBR />
                        <Typography color="secondary" variant="body1">
                        תחומים ותפקידים המשויכים למשרה:
                        </Typography>
                        <CustomBR h={10} />
                        {jobTags.map(tag => <RemovableTag
                            key={tag.tagid}
                            text={tag.tag}
                            onRemoveClick={() => setJobTags(jobTags.filter(t => t.tagid !== tag.tagid))}
                        />)}
                    </> : null}
                    <CustomBR />
                    <TagsSearchAutocomplete
                        filterItems={jobTags?.map(t => t.tag)}
                        clearOnChange
                        onChange={(_ev, val: TagViewModel) => { setJobTags([...jobTags, val]); }} />
                    {filteredRecommnededTags.length > 0 ? <div>
                        <CustomBR h={10} />
                        <Typography color="secondary" variant="body1">הצעות לתגיות:</Typography>
                        <CustomBR h={10} />
                        {filteredRecommnededTags.map(tag => <AddableTag
                            key={`addable-${tag.tagid}`}
                            text={tag.tag}
                            onAddClick={() => { setJobTags([...jobTags, tag]); }}
                        />)}
                    </div> : ''}
                </ShadowBGContainer>
                <CustomBR />
                <Button size="large" onClick={handleSubmit(onSubmit)} color="primary" fullWidth>
                    {editMode ? 'שמירת פרטי המשרה' : 'פרסום משרה'}
                </Button>
                <CustomBR /></>}
        </Container>
        <CustomBR />
    </Page>;
};

export default UserPostJobPage;
